import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useStores } from "../../context/StoresContext";
import { sectionsService } from "../../Api/Administracion/Almacenes";
import { UseAdmin } from "../UseAdmin";
import { UseUser } from "../../hooks";
import { handlerError, isDuplicateStoreName } from "../../utils/util";

/**
 * Hook de operaciones de almacenes.
 */
export const useStoreOperations = () => {
  const {
    stores,
    setStores,
    resetStores,
    updateStoreById,
    addStore,
    removeStore,
    setSelectedStoreData,
  } = useStores();

  const { brandfcNombre: brand,branchFiSucursalId } = UseAdmin();

  // Evitar recalcular en cada render
  const isRecalculatingRef = useRef(false);

  //* Estados por operación
  const [fetchState, setFetchState] = useState({ loading: false, error: null });
  const [createState, setCreateState] = useState({
    loading: false,
    error: null,
  });
  const [updateState, setUpdateState] = useState({
    loading: false,
    error: null,
  });
  const [deleteState, setDeleteState] = useState({
    loading: false,
    error: null,
  });
  const [toggleState, setToggleState] = useState({
    loading: false,
    error: null,
  });

  //* Utilidades
  // Recalcula el productCount del almacén "Todos" (defecto)
  const recalculateTodosCount = useCallback(() => {
    if (isRecalculatingRef.current) return; // Evitar recursión

    isRecalculatingRef.current = true;

    const totalCount = stores
      .filter((s) => s.id !== 0)
      .reduce((acc, s) => acc + (s.productCount || 0), 0);

    updateStoreById(0, { productCount: totalCount });

    // Liberar después de un tick
    setTimeout(() => {
      isRecalculatingRef.current = false;
    }, 0);
  }, [stores, updateStoreById]);

  // Auto-recalcular "Todos" cuando cambian los stores
  useEffect(() => {
    const hasTodos = stores.some((s) => s.id === 0);
    const todosStore = stores.find((s) => s.id === 0);

    if (hasTodos && todosStore) {
      const expectedCount = stores
        .filter((s) => s.id !== 0)
        .reduce((acc, s) => acc + (s.productCount || 0), 0);

      if (todosStore.productCount !== expectedCount) {
        recalculateTodosCount();
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [stores]);

  //* GET ALL
  const fetchStores = useCallback(async () => {
    setFetchState({ loading: true, error: null });

    try {
      const storesData = await sectionsService.getAll({
        brand,
        branchFiSucursalId,
        onlyActive: false,
      });

      const productsCount = storesData.reduce(
        (acc, store) => (store.active ? acc + (store.productCount ?? 0) : acc),
        0
      );

      const allStoresOption = {
        id: 0,
        name: "Todos",
        brand,
        active: true,
        productCount: productsCount,
        werksSap: "WERKS_000",
        lgortSap: "LGORT_000",
        lgobeSap: "LGOBE_000",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      setSelectedStoreData(allStoresOption);

      const allStores = [allStoresOption, ...storesData];

      setStores(allStores);

      setFetchState({ loading: false, error: null });

      return { success: true, data: allStores };
    } catch (err) {
      const errorMessage = err.message || "Error al obtener almacenes";
      setFetchState({ loading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [brand, resetStores, setSelectedStoreData]);

  //* CREATE
  const createAlmacen = useCallback(
    async (storeData) => {
      setCreateState({ loading: true, error: null });

      try {
        if (isDuplicateStoreName(stores, storeData.name)) {
          throw new Error("Ya existe un almacén con ese nombre");
        }

        const newStore = await sectionsService.create({
          brand,
          ceco:branchFiSucursalId,
          ...storeData,
        });

        addStore(newStore);

        setCreateState({ loading: false, error: null });
        return { success: true, data: newStore };
      } catch (err) {
        const errorMessage = err.message || "Error al crear almacén";
        setCreateState({ loading: false, error: errorMessage });
        return { success: false, error: errorMessage };
      }
    },
    [brand, branchFiSucursalId, stores, addStore]
  );

  //* UPDATE
  const updateAlmacen = useCallback(
    async (id, updates) => {
      setUpdateState({ loading: true, error: null });

      try {
        if (updates.name && isDuplicateStoreName(stores, updates.name, id)) {
          throw new Error("Ya existe un almacén con ese nombre");
        }

        const updatedStore = await sectionsService.update(id, {
          brand,
          ...updates,
        });

        updateStoreById(id, updatedStore);

        setUpdateState({ loading: false, error: null });
        return { success: true, data: updatedStore };
      } catch (err) {
        const errorMessage = err.message || "Error al actualizar almacén";
        setUpdateState({ loading: false, error: errorMessage });
        return { success: false, error: errorMessage };
      }
    },
    [brand, stores, updateStoreById]
  );

  //* TOGGLE STATUS
  const toggleAlmacenStatus = useCallback(
    async (id) => {
      setToggleState({ loading: true, error: null });

      const store = stores.find((store) => store.id === id);

      if (!store) {
        const error = `Almacén no encontrado: ${id}`;
        setToggleState({ loading: false, error });
        return { success: false, error };
      }

      const previousStatus = store.active;
      const newStatus = !previousStatus;

      updateStoreById(id, { active: newStatus });

      try {
        const response = await sectionsService.updateStatus(id, {
          brand,
          newStatus,
        });

        if (!response || response.error) {
          const error = response.error || "Error al actualizar estado del almacen";
          setToggleState({ loading: false, error });
          return { success: false, error };
        }

        setToggleState({ loading: false, error: null });

        return {
          success: true,
          data: { id, active: newStatus, previous: previousStatus },
        };
      } catch (err) {
        console.error("🟥 Error al cambiar status, revertiendo...", err);

        updateStoreById(id, { active: previousStatus });

        let errorMessage = "Error al cambiar estado";
        errorMessage = handlerError(err, errorMessage);

        setToggleState({ loading: false, error: errorMessage });

        return {
          success: false,
          error: errorMessage,
          data: {
            rolledBack: true,
            previous: previousStatus,
          },
        };
      }
    },
    [stores, brand, updateStoreById]
  );

  //* DELETE
  const deleteAlmacen = useCallback(
    async (id) => {
      setDeleteState({ loading: true, error: null });

      try {
        await sectionsService.delete(id);

        removeStore(id);

        setDeleteState({ loading: false, error: null });
        return { success: true, data: id };
      } catch (err) {
        const errorMessage = err.message || "Error al eliminar almacén";
        setDeleteState({ loading: false, error: errorMessage });
        return { success: false, error: errorMessage };
      }
    },
    [removeStore]
  );

  //* COMPUTED VALUES
  const stats = useMemo(() => {
    const rawStores = stores.filter((s) => s.id !== 0);
    const activeStores = rawStores.filter((s) => s.active);
    const inactiveStores = rawStores.filter((s) => !s.active);

    return {
      rawStores,
      total: rawStores.length,
      activos: activeStores.length,
      inactivos: inactiveStores.length,
    };
  }, [stores]);

  const activeStores = useMemo(() => stores.filter((s) => s.active && s.id !== 0), [stores]);

  const inactiveStores = useMemo(() => stores.filter((s) => !s.active && s.id !== 0), [stores]);

  //* RESET ERRORS
  const resetFetchError = useCallback(() => {
    setFetchState((prev) => ({ ...prev, error: null }));
  }, []);

  const resetCreateError = useCallback(() => {
    setCreateState((prev) => ({ ...prev, error: null }));
  }, []);

  const resetUpdateError = useCallback(() => {
    setUpdateState((prev) => ({ ...prev, error: null }));
  }, []);

  const resetDeleteError = useCallback(() => {
    setDeleteState((prev) => ({ ...prev, error: null }));
  }, []);

  const resetToggleError = useCallback(() => {
    setToggleState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    operations: {
      fetchStores,
      createAlmacen,
      updateAlmacen,
      deleteAlmacen,
      toggleAlmacenStatus,
    },
    states: {
      fetchState,
      createState,
      updateState,
      deleteState,
      toggleState,
    },
    resetErrors: {
      resetFetchError,
      resetCreateError,
      resetUpdateError,
      resetDeleteError,
      resetToggleError,
    },
    computed: {
      stats,
      activeStores,
      inactiveStores,
    },
  };
};
